import { FC, ReactElement, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@zoopla-eng/zds-components/button';
import { ColouredCard } from '@zoopla-eng/zds-components/coloured-card';
import { Box } from '@zoopla-eng/zds-components/box';
import { Divider } from '@zoopla-eng/zds-components/divider';

import { SocialAuthProps } from '../AuthModal.types';
import {
    questionsMap,
    YourSituation,
    questionsMapForSeller,
    YourSituationProps
} from '@zoopla/onboarding/your-situation';
import { StyledSubtitle, StyledTitle } from './YourSituationQuestion.styles';
import { Result } from './YourSituationResult';
import { trackQuestionSkip } from '../AuthModal.tracking';

type QuestionType = 'default' | 'seller' | 'update';

export type YourSituationQuestionProps = {
    page: string;
    onClose: (result?: ResultState) => void;
    title?: string;
    trackingIdentifier: 'update: ' | 'update: auth: ' | 'auth: ' | '';
    actionElement: ReactElement;
    questionType?: QuestionType;
} & Partial<SocialAuthProps>;

type ResultState = {
    goal?: string;
    lastQuestionId?: string;
};

type SituationTypes = {
    [key in QuestionType]: {
        questionsMap: YourSituationProps['questionsMap'];
        firstQuestionId: YourSituationProps['firstQuestionId'];
        banner?: string;
        successMessage?: string;
    };
};

export const YourSituationQuestion: FC<
    React.PropsWithChildren<YourSituationQuestionProps>
> = ({
    onClose,
    title,
    page,
    actionElement,
    questionType = 'default',
    trackingIdentifier = ''
}) => {
    const resultRef = useRef<HTMLDivElement>(null);
    const situationHeader = useRef<HTMLHeadingElement>(null);
    const formFocusRef = useRef<{ focus: () => void }>(null);
    const [isFirstQuestionFilled, setIsFirstQuestionFilled] = useState(false);
    const userProfileStorage = sessionStorage.getItem('auth-user-profile');
    const userProfileData = JSON.parse(userProfileStorage || '{}');

    const situationMap: SituationTypes = {
        default: {
            questionsMap,
            firstQuestionId: 'situationWhatIsYourCurrentPropertyGoal'
        },
        update: {
            questionsMap,
            firstQuestionId: 'situationWhatIsYourCurrentPropertyGoal',
            successMessage:
                "Thanks for updating us. We'll show more of what you need and less of what you don't."
        },
        seller: {
            questionsMap:
                questionsMapForSeller as YourSituationProps['questionsMap'],
            firstQuestionId: 'situationIsItAlreadyOnTheMarket',
            banner: 'You last told us that you’re selling a property but it’s not yet listed.',
            successMessage:
                "Thanks for updating us. We'll show more of what you need and less of what you don't."
        }
    };
    const questions = situationMap?.[questionType];

    const onSkipButtonClicked = () => {
        // the skip button is only shown on the first question
        trackQuestionSkip(questions?.firstQuestionId, page);
        onClose();
    };

    const onFirstQuestionFilled = () => setIsFirstQuestionFilled(true);

    const onResults = () => {
        setTimeout(() => {
            resultRef.current?.focus();
        }, 200);
    };

    useLayoutEffect(() => {
        if (title && situationHeader?.current) {
            setTimeout(() => {
                situationHeader.current?.focus();
            }, 200);
        } else {
            setTimeout(() => {
                formFocusRef.current?.focus?.();
            }, 200);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="your-situation-question">
            {/** This should probably go into the pages */}
            {title && (
                <Box pb="$M100">
                    <StyledTitle
                        data-testid="your-situation-heading"
                        tabIndex={-1}
                        ref={situationHeader}
                    >
                        {title}
                    </StyledTitle>
                </Box>
            )}
            {/** This should probably go into the pages */}

            {questions?.banner && !isFirstQuestionFilled && (
                <Box pb="$M200">
                    <ColouredCard backgroundColor="$graphicRentingLight">
                        {questions?.banner}
                    </ColouredCard>
                </Box>
            )}

            {isFirstQuestionFilled && (
                <Box pb="$S400">
                    <StyledSubtitle>My situation</StyledSubtitle>
                </Box>
            )}

            <YourSituation
                questionsMap={questions?.questionsMap}
                firstQuestionId={questions?.firstQuestionId}
                onFirstQuestionSaved={onFirstQuestionFilled}
                analyticsPage={page}
                actionAfterQuestionsElement={
                    <Result
                        actionElement={actionElement}
                        ref={resultRef}
                        successMessage={questions?.successMessage}
                    />
                }
                onResults={onResults}
                showEditButton
                ref={formFocusRef}
                forceFormMode="force_edit"
                trackingIdentifier={trackingIdentifier}
                initialValues={
                    questionType === 'update' ? userProfileData?.profile : null
                }
            />

            {!isFirstQuestionFilled && questionType === 'default' && (
                <Box>
                    <Box pt="$M100" pb="$S400">
                        <Divider direction="horizontal" />
                    </Box>
                    <Box display="flex" justifyContent="center" fw>
                        <Button
                            data-testid="skip-button"
                            category="tertiary"
                            onClick={onSkipButtonClicked}
                        >
                            Skip for now
                        </Button>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default YourSituationQuestion;
